import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, CheckSquare, FileText, MessageSquare } from "lucide-react";

interface FabMenuProps {
    onOpenModal: (type: "task" | "note" | "checkin") => void;
}

const FabMenu: React.FC<FabMenuProps> = ({ onOpenModal }) => {
    const [showFabMenu, setShowFabMenu] = useState(false);
    const fabRef = useRef<HTMLDivElement>(null);

    // Close FAB menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
                setShowFabMenu(false);
            }
        };

        if (showFabMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showFabMenu]);

    const handleOpenModal = (type: "task" | "note" | "checkin") => {
        onOpenModal(type);
        setShowFabMenu(false);
    };

    return (
        <div ref={fabRef} className="fixed bottom-6 right-6 z-50">
            {/* FAB Menu */}
            <AnimatePresence>
                {showFabMenu && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.8 }}
                        className="absolute bottom-20 right-0 bg-white rounded-xl border border-gray-200/60 shadow-xl p-2 w-52"
                    >
                        <button
                            onClick={() => handleOpenModal("task")}
                            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 rounded-lg transition-colors text-left"
                        >
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                            <span className="font-medium">New Task</span>
                        </button>
                        <button
                            onClick={() => handleOpenModal("note")}
                            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 rounded-lg transition-colors text-left"
                        >
                            <FileText className="w-5 h-5 text-green-600" />
                            <span className="font-medium">New Note</span>
                        </button>
                        <button
                            onClick={() => handleOpenModal("checkin")}
                            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 rounded-lg transition-colors text-left"
                        >
                            <MessageSquare className="w-5 h-5 text-purple-600" />
                            <span className="font-medium">New Check-in</span>
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* FAB Button */}
            <motion.button
                onClick={() => setShowFabMenu(!showFabMenu)}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
                animate={{ rotate: showFabMenu ? 45 : 0 }}
            >
                <Plus className="w-6 h-6 sm:w-7 sm:h-7" />
            </motion.button>
        </div>
    );
};

export default FabMenu;
